import { forwardRef, useEffect, useId, useImperativeHandle, useState } from 'react';

import { useTranslation } from 'react-i18next';

import './BillingContactCard.scss';

import { ReactComponent as CheckIcon } from '../../../../assets/icons/check.svg';
import {
  CLICK_ACTION,
  OPTION_NEW_CONTACT,
  OPTION_SAME_CONTACT,
  SAVE_BILLING_CONTACT
} from '../../../constants/tagManager';
import type {
  IBillingDraftFields,
  IContactDraftFields,
  ICreateProspectContact,
  IProspect,
  IProspectContact,
  IUpdateProspectContact
} from '../../../interfaces/prospects';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import { ProspectContactType } from '../../../types/prospects';
import { getPrimaryContact, isSameAsPrimaryContact } from '../../../utils/prospects/prospects';
import { isDraftEqual, trimmedOrUndefined } from '../../../utils/utils';
import { validateEmail } from '../../../utils/validation/validation';
import ContactDetails from '../ContactDetails/ContactDetails';
import ContactFields from '../ContactFields/ContactFields';

interface IBillingContactCardProps {
  prospect: IProspect;
  onCreateContact: (input: ICreateProspectContact) => Promise<void>;
  onUpdateContact: (contactId: number, input: IUpdateProspectContact) => Promise<void>;
  editing: boolean;
  onValidChange: (valid: boolean) => void;
  onSameAsPrimaryChange?: (same: boolean) => void;
  onDraftChange?: (draft: IBillingDraftFields | null) => void;
  getPrimaryDraft?: () => IContactDraftFields | null;
  disabled?: boolean;
}

interface IBillingDraft {
  sameAsPrimary: boolean;
  name: string;
  title: string;
  email: string;
  phone: string;
}

const draftFromContact = (contact: IProspectContact | undefined, prospect: IProspect): IBillingDraft => ({
  sameAsPrimary: !contact || isSameAsPrimaryContact(contact, prospect),
  name: contact?.name ?? '',
  title: contact?.title ?? '',
  email: contact?.email ?? '',
  phone: contact?.phone ?? ''
});

const fieldsFromPrimary = (prospect: IProspect): (IUpdateProspectContact & { name: string }) | null => {
  const primary = getPrimaryContact(prospect);
  if (!primary) return null;
  return { name: primary.name, title: primary.title, email: primary.email, phone: primary.phone };
};

const fieldsFromDraft = (draft: IBillingDraft): (IUpdateProspectContact & { name: string }) | null => {
  if (!draft.name.trim()) return null;
  return {
    name: draft.name.trim(),
    title: trimmedOrUndefined(draft.title),
    email: trimmedOrUndefined(draft.email),
    phone: trimmedOrUndefined(draft.phone)
  };
};

const fieldsFromContactDraft = (d: IContactDraftFields): (IUpdateProspectContact & { name: string }) | null => {
  if (!d.name.trim()) return null;
  return {
    name: d.name.trim(),
    title: trimmedOrUndefined(d.title),
    email: trimmedOrUndefined(d.email),
    phone: trimmedOrUndefined(d.phone)
  };
};

const fieldsFromPersistedContact = (contact: IProspectContact): IUpdateProspectContact & { name: string } => ({
  name: contact.name.trim(),
  title: trimmedOrUndefined(contact.title),
  email: trimmedOrUndefined(contact.email),
  phone: trimmedOrUndefined(contact.phone)
});

const shouldSkipPersist = (
  contact: IProspectContact | undefined,
  fields: IUpdateProspectContact & { name: string }
): boolean => {
  if (!contact) return false;
  return isDraftEqual(fields, fieldsFromPersistedContact(contact));
};

const BillingContactCard = forwardRef<{ save: () => Promise<void> }, IBillingContactCardProps>(
  (
    {
      prospect,
      onCreateContact,
      onUpdateContact,
      editing,
      onValidChange,
      onSameAsPrimaryChange,
      onDraftChange,
      getPrimaryDraft,
      disabled = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const sameAsPrimaryId = useId();
    const contact = prospect.contacts.find((c) => c.contactType === ProspectContactType.Billing);
    const [draft, setDraft] = useState<IBillingDraft>(() => draftFromContact(contact, prospect));

    useEffect(() => {
      onSameAsPrimaryChange?.(draft.sameAsPrimary);
    }, [draft.sameAsPrimary, onSameAsPrimaryChange]);

    useEffect(() => {
      onDraftChange?.(editing ? draft : null);
    }, [editing, draft, onDraftChange]);

    const nameMissing = !draft.sameAsPrimary && !draft.name.trim();
    const titleMissing = !draft.sameAsPrimary && !draft.title.trim();
    const emailMissing = !draft.sameAsPrimary && !draft.email.trim();
    const phoneMissing = !draft.sameAsPrimary && !draft.phone.trim();
    const emailInvalid = !!draft.email.trim() && !validateEmail(draft.email.trim());
    const isValid =
      draft.sameAsPrimary || (!nameMissing && !titleMissing && !emailMissing && !phoneMissing && !emailInvalid);

    useEffect(() => {
      onValidChange(isValid);
    }, [isValid, onValidChange]);

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          const persist = async (
            fields: IUpdateProspectContact & { name: string },
            option: typeof OPTION_SAME_CONTACT | typeof OPTION_NEW_CONTACT
          ) => {
            if (shouldSkipPersist(contact, fields)) return;
            if (contact) {
              await onUpdateContact(contact.id, fields);
            } else {
              await onCreateContact({ contactType: ProspectContactType.Billing, ...fields });
            }
            TagManagerService.pushEvent(CLICK_ACTION, SAVE_BILLING_CONTACT, option);
          };

          if (draft.sameAsPrimary) {
            const fields =
              fieldsFromContactDraft(getPrimaryDraft?.() ?? { name: '', title: '', email: '', phone: '' }) ??
              fieldsFromPrimary(prospect);
            if (!fields) return;
            await persist(fields, OPTION_SAME_CONTACT);
            return;
          }

          const fields = fieldsFromDraft(draft);
          if (!fields) return;
          await persist(fields, OPTION_NEW_CONTACT);
        }
      }),
      [contact, draft, prospect, onCreateContact, onUpdateContact, getPrimaryDraft]
    );

    const set = <K extends keyof IBillingDraft>(key: K, value: IBillingDraft[K]) =>
      setDraft((prev) => ({ ...prev, [key]: value }));

    if (editing) {
      return (
        <div className="profile-form">
          <div className="billing-same-toggle">
            <input
              id={sameAsPrimaryId}
              type="checkbox"
              className="billing-same-toggle__input"
              checked={draft.sameAsPrimary}
              disabled={disabled}
              onChange={(event) =>
                event.target.checked
                  ? set('sameAsPrimary', true)
                  : setDraft({ sameAsPrimary: false, name: '', title: '', email: '', phone: '' })
              }
            />
            <label className="billing-same-toggle__control" htmlFor={sameAsPrimaryId}>
              <span
                className={`billing-same-toggle__box${draft.sameAsPrimary ? ' billing-same-toggle__box--checked' : ''}`}
              >
                {draft.sameAsPrimary && <CheckIcon className="billing-same-toggle__check" width={10} height={10} />}
              </span>
              <span className="billing-same-toggle__label">{t(TRANSLATIONS_PROSPECTS.profile.form.sameAsPrimary)}</span>
            </label>
          </div>

          {!draft.sameAsPrimary && (
            <ContactFields
              draft={draft}
              onChange={(key, value) => set(key, value)}
              namePlaceholder={t(TRANSLATIONS_PROSPECTS.forms.billingName.placeholder)}
              emailPlaceholder={t(TRANSLATIONS_PROSPECTS.forms.billingEmail.placeholder)}
              requiredTitle={true}
              emailRequired={true}
              requiredPhone={true}
              disabled={disabled}
            />
          )}
        </div>
      );
    }

    return (
      <>
        {!contact && (
          <p className="profile-card__empty-hint">{t(TRANSLATIONS_PROSPECTS.profile.billingContact.empty)}</p>
        )}
        {contact &&
          isSameAsPrimaryContact(contact, prospect) &&
          (() => {
            const prim = getPrimaryContact(prospect);
            return (
              <div className="billing-same-display">
                <span className="billing-same-display__title">
                  {t(TRANSLATIONS_PROSPECTS.profile.form.sameAsPrimary)}
                </span>
                {prim && (
                  <span className="billing-same-display__sub">
                    {prim.name}
                    {prim.email ? ` · ${prim.email}` : ''}
                  </span>
                )}
              </div>
            );
          })()}
        {contact && !isSameAsPrimaryContact(contact, prospect) && (
          <ContactDetails contact={contact} requiredTitle={true} />
        )}
      </>
    );
  }
);

BillingContactCard.displayName = 'BillingContactCard';
export default BillingContactCard;
